import speakingurl from "speakingurl";

/**
 * Generates a URL-safe identifier slug from a string
 * Used to create unique placeholder prefixes from document names
 * Uses the same slugify implementation as Sanity Studio
 *
 * @param text - The text to slugify
 * @returns URL-safe slug
 *
 * @example
 * ```typescript
 * slugify("Alzheimer's & Dementia Care"); // "alzheimers-dementia-care"
 * slugify("24-Hour Care"); // "24-hour-care"
 * ```
 */
export function slugify(text: string): string {
  return speakingurl(text, { separator: "-" });
}

/**
 * Gets an identifying name from a document for use in placeholder keys
 * Tries common name fields in order of preference
 *
 * @param document - The Sanity document
 * @returns A slugified identifier or empty string if none found
 *
 * @example
 * ```typescript
 * getDocumentIdentifier({ serviceName: "Alzheimer's Care" }); // "alzheimers-care"
 * getDocumentIdentifier({ title: "Downtown Office" }); // "downtown-office"
 * getDocumentIdentifier({ name: "Springfield" }); // "springfield"
 * ```
 */
export function getDocumentIdentifier(
  document: Record<string, unknown>,
): string {
  // Try common identifying fields in order of preference
  const identifyingFields = [
    "serviceName",
    "title",
    "name",
    "locationName",
    "displayName",
  ];

  for (const field of identifyingFields) {
    const value = document[field];
    if (typeof value === "string" && value.trim()) {
      return slugify(value);
    }
  }

  return "";
}
